// ============================================================================
// Support Ticket Types - Matching backend DTOs
// ============================================================================

export enum TicketStatus {
  OPEN = 'OPEN',
  IN_PROGRESS = 'IN_PROGRESS',
  WAITING_FOR_CUSTOMER = 'WAITING_FOR_CUSTOMER',
  RESOLVED = 'RESOLVED',
  CLOSED = 'CLOSED',
}

export enum TicketPriority {
  LOW = 'LOW',
  MEDIUM = 'MEDIUM',
  HIGH = 'HIGH',
  URGENT = 'URGENT',
}

export enum TicketCategory {
  BOOKING = 'BOOKING',
  PAYMENT = 'PAYMENT',
  REFUND = 'REFUND',
  ACCOUNT = 'ACCOUNT',
  TECHNICAL = 'TECHNICAL',
  GENERAL = 'GENERAL',
}

export enum EscalationStatus {
  NONE = 'NONE',
  AGENT_REQUESTED_ADMIN_REVIEW = 'AGENT_REQUESTED_ADMIN_REVIEW',
  ADMIN_ACKNOWLEDGED = 'ADMIN_ACKNOWLEDGED',
  ADMIN_IN_PROGRESS = 'ADMIN_IN_PROGRESS',
  ADMIN_RESOLVED = 'ADMIN_RESOLVED',
}

export enum MessageSenderType {
  USER = 'USER',
  AGENT = 'AGENT',
  SYSTEM = 'SYSTEM',
}

export enum MessageChannel {
  APP = 'APP',
  INTERNAL_NOTE = 'INTERNAL_NOTE',
}

export enum CallDirection {
  INBOUND = 'INBOUND',
  OUTBOUND = 'OUTBOUND',
}

export enum CallStatus {
  INITIATED = 'INITIATED',
  RINGING = 'RINGING',
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETED = 'COMPLETED',
  BUSY = 'BUSY',
  NO_ANSWER = 'NO_ANSWER',
  FAILED = 'FAILED',
  CANCELED = 'CANCELED',
}

// ============================================================================
// User Types
// ============================================================================

export interface TicketUser {
  id: number;              // User.id is Int in the database
  name?: string;           // Backend uses single 'name' field
  email?: string;
  phoneNumber?: string;
  profileImage?: string;   // Backend uses 'profileImage' not 'avatarUrl'
  bio?: string;
  gender?: string;
  pronouns?: string;
  dateOfBirth?: string;
  
  // Location
  address?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  country?: string;
  timezone?: string;
  
  // Account status
  isActive?: boolean;
  isVerified?: boolean;
  verificationDate?: string;
  lastSeenAt?: string;
  platform?: string;
  
  // Ratings
  customerRating?: number;
  customerRatingCount?: number;
  providerRating?: number;
  providerRatingCount?: number;
  
  // Household
  householdId?: string;
  relationshipToOwner?: string;
  
  // Stats
  _count?: {
    bookingsAsCustomer: number;
    bookingsAsProvider: number;
  };
  
  userType: 'PARENT' | 'PROVIDER';
  createdAt: string;
}

export interface Agent {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  avatarUrl?: string;
}

// ============================================================================
// Ticket Types
// ============================================================================

export interface Ticket {
  id: string;
  userId: string;
  title: string;
  status: TicketStatus;
  priority: TicketPriority;
  category: TicketCategory;
  escalationStatus: EscalationStatus;
  escalationReason?: string;
  assignedToUserId?: string;
  createdAt: string;
  updatedAt: string;
  lastActivityAt: string;
  _count?: {
    messages: number;
    calls: number;
  };
  // Populated on detail view
  user?: TicketUser;
  assignedToUser?: Agent;
}

export interface TicketDetail extends Ticket {
  user?: TicketUser;  // Optional - fetched separately by backend
  assignedToUser?: Agent;
  messages: Message[];
  calls: SupportCall[];
  booking?: {
    id: string;
    status: string;
    scheduledAt: string;
    providerName: string;
  };
  payment?: {
    id: string;
    amount: number;
    status: string;
  };
  refund?: {
    id: string;
    amount: number;
    status: string;
  };
}

// ============================================================================
// Message Types
// ============================================================================

export interface Message {
  id: string;
  ticketId: string;
  body: string;              // Backend field name is 'body'
  channel: MessageChannel;
  senderType: MessageSenderType;
  direction: 'INBOUND' | 'OUTBOUND';
  senderUserId?: string;     // Backend field name is 'senderUserId'
  author?: Agent;            // Enriched by frontend if needed
  createdAt: string;
  updatedAt: string;
  isRead: boolean;
}

// ============================================================================
// Call Types
// ============================================================================

export interface SupportCall {
  id: string;
  ticketId?: string;
  direction: CallDirection;
  status: CallStatus;
  from: string;
  to: string;
  twilioCallSid: string;
  startedAt: string;
  endedAt?: string;
  durationSeconds?: number;
  recordingUrl?: string;
  createdAt: string;
}

// ============================================================================
// API Request/Response Types
// ============================================================================

export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

// Query params matching backend AdminQueryTicketsDto exactly
export interface TicketQueryParams {
  status?: TicketStatus;
  priority?: TicketPriority;
  category?: TicketCategory;
  escalationStatus?: EscalationStatus;
  assignedToUserId?: string;
  userId?: string;
  search?: string;
  page?: number;
  pageSize?: number;
}

// Alias for UI convenience (same as backend params)
export type TicketQueryParamsUI = TicketQueryParams;

export interface MessageQueryParams {
  page?: number;
  pageSize?: number;
  since?: string;
  afterMessageId?: string;
}

export interface UpdateTicketDto {
  status?: TicketStatus;
  priority?: TicketPriority;
  assignedToUserId?: string | null;
  escalationStatus?: EscalationStatus;
}

export interface AddMessageDto {
  content: string;
  type: 'APP' | 'INTERNAL_NOTE';
}

export interface EscalateTicketDto {
  reason: string;
  upgradePriorityToUrgent?: boolean;
}

// ============================================================================
// Voice/Call Types
// ============================================================================

export interface VoiceTokenResponse {
  token: string;
  identity: string;
  expiresIn?: number;  // Optional - may not be returned by all endpoints
}

export interface VoiceConfigResponse {
  configured: boolean;
  supportPhoneNumber: string;
  agentIdentity: string;
  sharedIdentity: string;
}

export interface OutboundCallInfo {
  ticketId: string;
  userId: string;
  phoneNumber: string;
  userName: string;
  ticketSubject: string;
}

// ============================================================================
// UI State Types
// ============================================================================

export type CallState = 'idle' | 'connecting' | 'ringing' | 'in-call' | 'incoming' | 'ended';

export interface TwilioCallContext {
  state: CallState;
  isMuted: boolean;
  duration: number;
  callerId?: string;      // The customer's phone number
  callerName?: string;    // Customer name if looked up
  ticketId?: string;      // Related ticket ID
  callSid?: string;       // Twilio Call SID
}
